import { useEffect } from 'react';

/**
 * Helper function to check if an error is a DOM manipulation error
 * caused by external DOM-mutating tools like Google Translate.
 */
export const isDOMConflictError = (error: any): boolean => {
  const message = typeof error === 'string' 
    ? error 
    : error instanceof ErrorEvent 
      ? error.message 
      : error?.message || '';
  const name = error instanceof Error ? error.name : '';
  
  return (
    name === 'NotFoundError' ||
    message.includes('insertBefore') ||
    message.includes('removeChild') ||
    message.includes('not a child of this node') ||
    message.includes('appendChild')
  );
};

/**
 * Hook to suppress DOM conflict errors during component lifecycle.
 * This version monkey-patches the DOM nodes to prevent fatal exceptions
 * during React's commit phase, which is necessary for external tools
 * (e.g. Google Translate) that move nodes outside of React's control.
 */
export const useDOMErrorSuppression = (isActive: boolean) => {
  useEffect(() => {
    if (!isActive) return;

    // 1. Window-level error suppression
    const originalErrorHandler = window.onerror;
    const originalUnhandledRejection = window.onunhandledrejection;

    window.onerror = (message, source, lineno, colno, error) => {
      if (error && isDOMConflictError(error)) {
        console.info('[DOMErrorSuppression] Suppressed DOM conflict error from external tool.');
        return true;
      }
      return originalErrorHandler?.call(window, message, source, lineno, colno, error) ?? false;
    };

    window.onunhandledrejection = (event) => {
      if (event.reason && isDOMConflictError(event.reason)) {
        console.info('[DOMErrorSuppression] Suppressed DOM conflict rejection from external tool.');
        event.preventDefault();
        return;
      }
      originalUnhandledRejection?.call(window, event);
    };

    // 2. Monkey-patch Node.prototype.removeChild
    // React calls this to unmount nodes. If an external tool moved the node, React fails.
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function<T extends Node>(child: T): T {
      if (child.parentNode !== this) {
        if (child.parentNode) {
          console.warn('[DOMErrorSuppression] removeChild: Node is not a child of this parent, but has a different parent. Removing from actual parent.', { child, expectedParent: this, actualParent: child.parentNode });
          return child.parentNode.removeChild(child);
        }
        console.warn('[DOMErrorSuppression] removeChild: Node is not a child of this parent and has no parent. Ignoring.', { child, parent: this });
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    // 3. Monkey-patch Node.prototype.insertBefore
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function<T extends Node>(newChild: T, refChild: Node | null): T {
      if (refChild && refChild.parentNode !== this) {
        console.warn('[DOMErrorSuppression] insertBefore: refChild is not a child of this parent. Appending to end instead.', { newChild, refChild, parent: this });
        return this.appendChild(newChild) as T;
      }
      return originalInsertBefore.call(this, newChild, refChild) as T;
    };

    return () => {
      window.onerror = originalErrorHandler;
      window.onunhandledrejection = originalUnhandledRejection;
      Node.prototype.removeChild = originalRemoveChild;
      Node.prototype.insertBefore = originalInsertBefore;
    };
  }, [isActive]);
};
